import { api } from '@/apis';
import { isEmpty, log } from '@/utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';

export function useVehicleBoundaryVisits({ boundary, query: filter }) {
  const queryKey = ['vehicleBoundaryVisits', boundary, filter];
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const { startTime, endTime, ...match } = filter;

      const featureRequestParams = {
        startTime: endTime.$gte,
        endTime: startTime.$lt,
        boundary: boundary,
        ...match,
      };

      const featuresRequest = api
        .post('boundary-visits/vehicles', {
          json: featureRequestParams,
          signal,
        })
        .json();

      const tripPipeline = [
        {
          $match: {
            ...filter,
            path: {
              $geoIntersects: {
                $geometry: boundary,
              },
            },
          },
        },
        {
          $project: {
            _id: false,
            identifier: true,
            equipmentActivations: { emergencyOn: true },
            driver: {
              code: true,
              forenames: true,
              surname: true,
              collarNumber: true,
              role: true,
            },
            vehicle: {
              telematicsBoxImei: true,
              registrationNumber: true,
              fleetNumber: true,
              role: true,
              type: true,
            },
            rfidCard: {
              reference: true,
              type: true,
              label: true,
            },
            startTime: true,
            endTime: true,
            durationSeconds: true,
            distanceMiles: {
              $multiply: ['$distanceKilometres', 0.62137119],
            },
            maxSpeedMilesPerHour: {
              $multiply: ['$maxSpeedKilometresPerHour', 0.62137119],
            },
          },
        },
      ];
      const tripsRequest = api
        .post('pipeline/trips', {
          json: tripPipeline,
          signal,
        })
        .json();

      const [features, trips] = await Promise.all([
        featuresRequest,
        tripsRequest,
      ]);

      log('Read', 'Vehicle Boundary Visits', { boundary, query: filter });

      return features.map((feature) => ({
        ...feature,
        properties: {
          ...feature.properties,
          trips: trips.filter(
            (trip) =>
              trip.vehicle?.telematicsBoxImei === feature.properties.imei &&
              trip.startTime < feature.properties.endTime &&
              trip.endTime > feature.properties.startTime,
          ),
        },
      }));
    },
    placeholderData: [],
    staleTime: 1000 * 60 * 60,
    enabled: !!boundary && !isEmpty(filter),
  });

  return {
    ...query,
    cancel: () => queryClient.cancelQueries({ queryKey }),
  };
}
